import { useState, useMemo } from 'react';
import { ArrowUpDown, Calendar, Eye, FileText, Filter, DollarSign, Clock, CheckCircle } from 'lucide-react';
import { useCreatorPortal } from '../../contexts/CreatorPortalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatEuro } from '../../utils/currency';
import type { CreatorCampaignData } from '../../services/creatorPortalService';

type StatusFilter = 'all' | 'ready' | 'awaiting' | 'paid';

function getCreatorPaymentBadge(campaign: CreatorCampaignData, t: (k: string) => string) {
  if (campaign.influencer_amount_paid >= campaign.net_earnings * 0.99 && campaign.influencer_amount_paid > 0) {
    return { label: t('creatorsHub.paymentStatus.paid'), cls: 'bg-green-100 text-green-700', sort: 3 };
  }
  if (campaign.net_payable > 0 && campaign.amount_received > 0) {
    return { label: t('creatorsHub.paymentStatus.readyForPayment'), cls: 'bg-emerald-100 text-emerald-700', sort: 0 };
  }
  if (campaign.company_invoice_status === 'Issued' || campaign.company_invoice_status === 'Overdue') {
    return { label: t('creatorsHub.billing.awaitingClient'), cls: 'bg-amber-100 text-amber-700', sort: 1 };
  }
  return { label: t('creatorsHub.paymentStatus.pending'), cls: 'bg-gray-100 text-gray-600', sort: 2 };
}

function formatDate(d: string | null) {
  if (!d) return '-';
  return new Date(d + 'T00:00:00').toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function CreatorBillingView() {
  const { t } = useLanguage();
  const { campaigns, setSelectedCampaignId } = useCreatorPortal();
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const relevantCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      const s = c.company_invoice_status;
      return s === 'Issued' || s === 'Paid' || s === 'Overdue' ||
        (c.net_payable > 0 && c.amount_received > 0);
    });
  }, [campaigns]);

  const availableYears = useMemo(() => {
    const years = new Set(relevantCampaigns.map(c => c.year));
    return Array.from(years).sort((a, b) => b - a);
  }, [relevantCampaigns]);

  const filtered = useMemo(() => {
    let result = relevantCampaigns;
    if (selectedYear !== 'all') {
      result = result.filter(c => c.year === selectedYear);
    }
    if (statusFilter !== 'all') {
      result = result.filter(c => {
        const badge = getCreatorPaymentBadge(c, t);
        if (statusFilter === 'ready') return badge.sort === 0;
        if (statusFilter === 'awaiting') return badge.sort === 1;
        if (statusFilter === 'paid') return badge.sort === 3;
        return true;
      });
    }
    return [...result].sort((a, b) => {
      const dateA = a.company_invoice_issued_date || a.updated_at || '';
      const dateB = b.company_invoice_issued_date || b.updated_at || '';
      return sortAsc ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
  }, [relevantCampaigns, selectedYear, statusFilter, sortAsc, t]);

  const totalReady = filtered.reduce((s, c) => s + c.net_payable, 0);
  const totalPaid = filtered.reduce((s, c) => s + c.influencer_amount_paid, 0);

  if (relevantCampaigns.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">{t('creatorsHub.noBillingCampaigns')}</p>
      </div>
    );
  }

  const statusFilters: { key: StatusFilter; label: string; icon: React.ElementType; color: string }[] = [
    { key: 'all', label: t('creatorsHub.filterAll'), icon: FileText, color: '' },
    { key: 'ready', label: t('creatorsHub.billing.filterReady'), icon: DollarSign, color: 'text-emerald-600' },
    { key: 'awaiting', label: t('creatorsHub.billing.filterAwaiting'), icon: Clock, color: 'text-amber-600' },
    { key: 'paid', label: t('creatorsHub.paymentStatus.paid'), icon: CheckCircle, color: 'text-green-600' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('creatorsHub.billing.title')}</h2>
          <p className="text-sm text-gray-500">
            {filtered.length} {t('creatorsHub.billingSubtitle')}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-gray-400 uppercase tracking-wider">{t('creatorsHub.billing.youReceived')}</p>
            <p className="text-lg font-bold text-emerald-600">{formatEuro(totalPaid)}</p>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="text-right">
            <p className="text-xs text-gray-400 uppercase tracking-wider">{t('creatorsHub.billing.pending')}</p>
            <p className="text-lg font-bold text-amber-600">{formatEuro(totalReady)}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={selectedYear}
          onChange={e => setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
        >
          <option value="all">{t('creatorsHub.filterAll')}</option>
          {availableYears.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <div className="flex items-center gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
          <Filter className="w-3 h-3 text-gray-400 ml-1.5" />
          {statusFilters.map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`text-xs font-medium px-2.5 py-1 rounded-xl transition-colors ${
                statusFilter === f.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setSortAsc(!sortAsc)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowUpDown className="w-3.5 h-3.5" />
          {sortAsc ? t('creatorsHub.sortOldest') : t('creatorsHub.sortNewest')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_130px_110px_110px_110px_48px] gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
          <span>{t('creatorsHub.table.campaign')}</span>
          <span>{t('creatorsHub.billingDate')}</span>
          <span className="text-right">{t('creatorsHub.billing.myShare')}</span>
          <span className="text-right">{t('creatorsHub.billing.received')}</span>
          <span className="text-right">{t('creatorsHub.table.paymentStatus')}</span>
          <span />
        </div>

        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">{t('creatorsHub.billing.noMatch')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map(c => {
              const badge = getCreatorPaymentBadge(c, t);
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedCampaignId(c.id)}
                  className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors group sm:grid sm:grid-cols-[1fr_130px_110px_110px_110px_48px] sm:gap-3 sm:items-center"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {c.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{c.brand}</p>
                  </div>

                  <div className="flex items-center gap-1.5 text-sm text-gray-600 mt-2 sm:mt-0">
                    <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    {formatDate(c.company_invoice_issued_date)}
                  </div>

                  <div className="text-right mt-2 sm:mt-0">
                    <p className="text-sm font-semibold text-gray-900">{formatEuro(c.net_earnings)}</p>
                  </div>

                  <div className="text-right mt-2 sm:mt-0">
                    <p className="text-sm font-semibold text-emerald-600">
                      {c.influencer_amount_paid > 0 ? formatEuro(c.influencer_amount_paid) : '-'}
                    </p>
                  </div>

                  <div className="text-right mt-2 sm:mt-0">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-xl ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </div>

                  <div className="hidden sm:flex justify-center">
                    <div className="w-7 h-7 rounded-lg bg-gray-50 group-hover:bg-blue-50 flex items-center justify-center transition-colors">
                      <Eye className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
